/**
 * Card — the widget shell used by every dashboard panel
 * (`Widget/RecentActivity` 12:3103, `Widget/InquiriesNeedingAction` 12:3101).
 *
 * Measured: `bg/surface` fill, 1px `border/default`, `radius/md` (12px), p-20,
 * 12px gap; the title is `H3/16` in `text/primary`.
 */

import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface CardProps {
  title?: ReactNode;
  /** Right-aligned control in the title row (a "View all" link, a menu). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Card({ title, action, children, className }: CardProps): React.JSX.Element {
  return (
    <section
      className={cn(
        'flex flex-col gap-3 rounded-md border border-line bg-surface p-5',
        className,
      )}
    >
      {title === undefined && action === undefined ? null : (
        <header className="flex items-center justify-between gap-3">
          {typeof title === 'string' ? (
            <h2 className="text-h3 text-fg">{title}</h2>
          ) : (
            title
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/**
 * The 6px chip from the country tile Meta row (node 29:51).
 *
 * ⚠ v22-C ACCESSIBILITY FIX — `bg-surface` + a ring, NOT `bg-subtle`. The
 * shipped chip wore `text/secondary` on `bg/subtle`, which MEASURES 4.40:1 in
 * light at 12px — the exact pair tokens.css records as "the pair this repo has
 * already refused twice" (`admin-users.contrast.test.ts`,
 * `quotes.contrast.test.ts`), shipped here regardless and inherited by every
 * chip on every screen. The cure is the country badge's own precedent: an
 * OPAQUE `bg-surface` fill with a `border-line` ring, which puts the text on
 * the pair the repo measures at 4.64 light / 5.85 dark — over the bar in both
 * modes, on every ground the chip sits on. The ring now draws in BOTH modes
 * (it was dark-only when the fill matched the dark surface); in light it is
 * the same hairline every card already wears.
 */
export function Chip({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[6px] bg-surface px-1.5 py-0.5 text-label text-fg-secondary',
        'ring-1 ring-line',
        className,
      )}
    >
      {children}
    </span>
  );
}
